import { useState, useRef, useEffect } from 'react';

function useInView() {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setInView(true); }, { threshold: 0.08 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return { ref, inView };
}

const testimonials = [
  {
    quote: "Mr. Gamble represented my son and my husband and I were very pleased with the outcome. Instead of going to prison, which the DA wanted, thanks to Mr. Gamble\'s efforts, Morgan was able to go to a recovery program. He kept us updated and was always available to take my calls or he would get right back to me. He is compassionate and kind. I highly recommend Mr. Gamble.",
    author: "Verified Client",
    rating: 5,
    area: 'Criminal Defense',
  },
  {
    quote: "Mr. Gamble got my case dismissed. He took a proactive approach to prove my innocence by helping me hire a highly qualified investigator to collect evidence on my behalf. He also obtained a certified polygraph specialist to administer my test in order to provide the DA with additional affirmation of my innocence. I persevered under their guidance and finally could put the ordeal behind me.",
    author: "Verified Client",
    rating: 5,
    area: 'Criminal Defense',
  },
  {
    quote: "I am incredibly grateful for the exceptional legal representation provided by my lawyer. Their expertise, dedication, and attention to detail were instrumental in achieving a successful outcome for my case. I highly recommend their services to anyone in need of top-notch legal assistance. And her assistant was really helpful in my needs.",
    author: "Verified Client",
    rating: 5,
    area: 'Civil Litigation',
  },
  {
    quote: "Working with the team of Gamble and Garcia was very good. We will hire them again if we have a future case and we will recommend them to all our friends and family. When it comes to financial issues or fees, it is affordable. Attorney Gamble said yes to accept the case and worked with us through every step.",
    author: "Verified Client",
    rating: 5,
    area: 'Family Law',
  },
  {
    quote: "Very Professional. The entire team at Gamble & Garcia handled my matter with the utmost professionalism and care. I felt supported throughout the entire process and the outcome was exactly what I was hoping for.",
    author: "Verified Client",
    rating: 5,
    area: 'Personal Injury',
  },
];

export default function Testimonials() {
  const [active, setActive] = useState(0);
  const { ref, inView } = useInView();

  const prev = () => setActive((a) => (a === 0 ? testimonials.length - 1 : a - 1));
  const next = () => setActive((a) => (a === testimonials.length - 1 ? 0 : a + 1));

  const t = testimonials[active];

  const scrollToContact = () => {
    const el = document.querySelector('#contact');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="testimonials" className="bg-[#F7F5F0] py-24 md:py-32 relative overflow-hidden" ref={ref}>
      {/* Large decorative quote mark */}
      <div className="absolute top-0 left-4 font-serif text-[#C9A84C]/5 text-[320px] leading-none select-none pointer-events-none">&ldquo;</div>

      <div className="relative z-10 max-w-[1100px] mx-auto px-6 md:px-12 lg:px-16">

        {/* Header */}
        <div className={`flex flex-col md:flex-row md:items-end md:justify-between mb-16 gap-6 transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-[2px] bg-[#C9A84C]"></div>
              <span className="text-[#C9A84C] text-[10px] tracking-[0.45em] uppercase font-medium">Client Feedback</span>
            </div>
            <h2 className="font-serif text-[#0D0D0D] text-4xl md:text-5xl font-bold leading-[1.1]">
              Client Outcomes<br />&amp; Feedback
            </h2>
          </div>
          <div className="flex flex-col items-start md:items-end gap-2.5">
            <div className="flex items-center gap-1">
              {[1,2,3,4,5].map(s => (
                <div key={s} className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-star-fill text-[#C9A84C] text-base"></i>
                </div>
              ))}
            </div>
            <span className="text-[#777] text-sm">4.8 Average Rating &bull; 50+ Verified Reviews</span>
            <a
              href="https://clientratings.martindale.com/clientrating/fillSurvey?firmId=300040859"
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="text-[#C9A84C] text-[11px] tracking-wider uppercase underline cursor-pointer hover:text-[#b8943e] transition-colors"
            >
              Leave a Review
            </a>
          </div>
        </div>

        {/* Testimonial card */}
        <div className={`transition-all duration-700 delay-200 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="bg-white p-10 md:p-14 relative border-l-[4px] border-[#C9A84C]">
            {/* Area badge */}
            <span className="inline-block px-3 py-1.5 bg-[#C9A84C]/10 text-[#C9A84C] text-[10px] tracking-[0.28em] uppercase font-semibold mb-6">
              {t.area}
            </span>

            {/* Stars */}
            <div className="flex items-center gap-1 mb-6">
              {Array.from({ length: t.rating }).map((_, i) => (
                <div key={i} className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-star-fill text-[#C9A84C] text-sm"></i>
                </div>
              ))}
            </div>

            {/* Quote */}
            <blockquote className="font-serif text-[#1A1A1A] text-xl md:text-2xl italic leading-[1.8] mb-8">
              &ldquo;{t.quote}&rdquo;
            </blockquote>

            {/* Author */}
            <div className="flex items-center gap-3 mb-3">
              <div className="w-8 h-[2px] bg-[#C9A84C]"></div>
              <span className="text-[#666] text-sm font-medium tracking-wider">{t.author}</span>
            </div>

            {/* Verified badge */}
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-verified-badge-fill text-[#C9A84C] text-sm"></i>
              </div>
              <span className="text-[#aaa] text-[11px] tracking-wider">Verified via Martindale-Hubbell</span>
            </div>
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between mt-8">
            <div className="flex gap-2 items-center">
              {testimonials.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setActive(i)}
                  className={`transition-all duration-300 rounded-full cursor-pointer ${i === active ? 'w-8 h-2 bg-[#C9A84C]' : 'w-2 h-2 bg-[#0D0D0D]/15 hover:bg-[#0D0D0D]/30'}`}
                  aria-label={`Go to testimonial ${i + 1}`}
                />
              ))}
            </div>
            <div className="flex gap-3">
              <button
                onClick={prev}
                className="w-11 h-11 flex items-center justify-center border border-[#0D0D0D]/15 hover:bg-[#0D0D0D] hover:text-white hover:border-[#0D0D0D] text-[#0D0D0D] transition-all duration-300 cursor-pointer"
                aria-label="Previous testimonial"
              >
                <i className="ri-arrow-left-line text-base"></i>
              </button>
              <button
                onClick={next}
                className="w-11 h-11 flex items-center justify-center border border-[#0D0D0D]/15 hover:bg-[#0D0D0D] hover:text-white hover:border-[#0D0D0D] text-[#0D0D0D] transition-all duration-300 cursor-pointer"
                aria-label="Next testimonial"
              >
                <i className="ri-arrow-right-line text-base"></i>
              </button>
            </div>
          </div>
        </div>

        {/* Post-testimonial CTA */}
        <div className={`mt-14 flex flex-col sm:flex-row items-center justify-between gap-6 p-8 bg-[#0D0D0D] transition-all duration-700 delay-400 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div>
            <p className="text-white font-serif text-xl font-bold">Ready to get results like these?</p>
            <p className="text-white/40 text-sm mt-1">Start with a free, no-obligation case evaluation.</p>
          </div>
          <button
            onClick={scrollToContact}
            className="flex-shrink-0 px-8 py-4 bg-[#C9A84C] text-black font-bold text-[11px] tracking-[0.25em] uppercase hover:bg-[#b8943e] transition-all duration-300 cursor-pointer whitespace-nowrap"
          >
            Get Legal Guidance Now
          </button>
        </div>
      </div>
    </section>
  );
}
